<template>
  <div style="width: 100%;height: 100%;">
    <div ref="chart" v-show="!empty" class="canvas-wrapper"></div>
    <Empty v-show="empty" />
  </div>
</template>

<script>
import { Chart } from '@antv/g2';
import { pieColorsDefault } from '@/config/colors';
import { pxtorem } from '@/libs/fontfit';

export default {
  props: {
    dataSource: {
      type: Array,
      default: () => [],
    },
    height: {
      type: Number,
      default: 0,
    },
    width: {
      type: Number,
      default: 0,
    },

    total: {
      type: Number,
      default: 0,
    },
    totalText: {
      type: String,
      default: '',
    },
    empty: {
      type: Boolean,
      default: false,
    },
    radius: {
      type: Number,
      default: 0.85,
    },
    innerRadius: {
      type: Number,
      default: 0.7,
    },
    colorList: {
      type: Array,
      // default: ['#165DFF', '#50CD89', '#F7BA1E', '#FF7C7C', '#9B9B9B'],
      default: () => [],
    },
    legend: {
      type: Boolean,
      default: false,
    },
    // 小数位
    decimal: {
      type: Number,
      default: 2,
    },
  },
  computed: {
    data() {
      // 使用 slice() 创建副本，避免直接修改 props
      return this.dataSource.slice();
    },
  },

  watch: {
    data(value) {
      if (value) {
        this.watchElementVisible(() => {
          this.createChart();
        });
      }
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  mounted() {
    this.watchElementVisible(() => {
      this.createChart();
    });
  },
  methods: {
    // 监听元素是否可见
    watchElementVisible(callback) {
      if ('IntersectionObserver' in window) {
        const targetNode = this.$refs.chart;
        // 创建 IntersectionObserver 实例
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                // 当 div 元素进入视口时
                callback && callback();
              } else {
                // 当 div 元素离开视口时
                // console.log('div 不再可见');
              }
            });
          },
          {
            root: null, // 默认是视口
            threshold: 0.1, // 50% 的元素在视口内时视为可见
          }
        );

        // 开始观察
        if (targetNode) {
          observer.observe(targetNode);
        }
      } else {
        setTimeout(() => {
          callback && callback();
        }, 500);
      }
    },

    createChart() {
      if (!this.total) return;
      const data = this.data || [];

      // 如果没有颜色，就使用默认颜色
      if (data.length > this.colorList.length) {
        this.data.forEach((item, index) => {
          if (this.colorList[index]) return;
          this.colorList[index] = pieColorsDefault[index] || 'black';
        });
      }

      // 参数
      const params = {
        container: this.$refs.chart,
        autoFit: true,
      };
      if (this.width) {
        params.width = this.width;
      }

      if (this.height) {
        params.height = pxtorem(this.height);
      }

      // 销毁
      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = new Chart(params);

      // 设置圆的大小
      this.chart.coordinate({
        type: 'theta',
        outerRadius: this.radius,
        innerRadius: this.innerRadius,
      });

      // 百分比总和
      let percentTotal = 0;

      // 设置
      this.chart
        .interval()
        .data(data)
        .transform({ type: 'stackY' })
        .encode('y', 'percent')
        .encode('color', 'item')
        .scale('color', {
          range: this.colorList,
        })
        .tooltip((data, index) => {
          // 简易计算，保证所有项的百分比总和为100%
          let _percent;
          if (index === data.length - 1) {
            _percent = 100 - percentTotal;
          } else {
            _percent = (data.percent * 100).toFixed(this.decimal);
            percentTotal += _percent;
          }

          return {
            name: data.item,
            value: `${_percent}%`,
            showCrosshairs: true,
            shared: true,
          };
        });

      // legend
      if (this.legend) {
        this.chart.legend('color', {
          position: 'bottom',
          layout: { justifyContent: 'center' },
        });
      } else {
        this.chart.legend(false);
      }

      this.chart.options({
        interaction: {
          tooltip: {
            // shared: true,
            mount: 'body',
            // css: {
            //   '.g2-tooltip': {
            //     background: '#eee',
            //     'border-radius': ' 0.25em !important',
            //   },
            // },
          },
        },
      });

      // 中心的文字：总数
      this.chart
        .text()
        .style('text', this.totalText)
        // Relative position
        .style('x', '50%')
        .style('y', '50%')
        .style('dy', -pxtorem(20))
        .style('fontSize', pxtorem(16))
        .style('fill', '#4E5969')
        .style('textAlign', 'center')
        .tooltip(false);

      // 中心的数值
      this.chart
        .text()
        .style('text', `${this.total}`)
        // Relative position
        .style('x', '50%')
        .style('y', '50%')
        .style('dy', 10)
        .style('fontSize', pxtorem(44))
        .style('fill', '#18181A')
        .style('textAlign', 'center')
        .tooltip(false);

      // this.chart
      //   .text()
      //   .style('text', '台')
      //   // Relative position
      //   .style('x', '50%')
      //   .style('y', '50%')
      //   .style('dx', 35)
      //   .style('dy', 25)
      //   .style('fontSize', 34)
      //   .style('fill', '#8c8c8c')
      //   .style('textAlign', 'center');

      this.chart.render();
    },
  },
};
</script>
<style lang="less" scoped>
.canvas-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}
</style>
